import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Event } from '../types';
import { EVENT_COLOR_MAP, EVENT_STATUS_COLORS, EVENT_STATUS_LABELS } from '../types';

// ── Types ──────────────────────────────────────────────────────────────────────

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: Event[];
}

interface EventCalendarProps {
  year: number;
  month: number;
  events: Event[];
  onPrev: () => void;
  onNext: () => void;
  onDayClick: (date: Date, events: Event[]) => void;
  onEventClick: (event: Event) => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

// ── Build calendar grid ────────────────────────────────────────────────────────

function buildGrid(year: number, month: number, events: Event[]): CalendarDay[] {
  const today    = new Date();
  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);

  // pad with days from previous month
  const startOffset = firstDay.getDay(); // 0=Sun
  const cells: CalendarDay[] = [];

  for (let i = startOffset - 1; i >= 0; i--) {
    const date = new Date(year, month, -i);
    cells.push({ date, isCurrentMonth: false, isToday: false, events: [] });
  }

  for (let d = 1; d <= lastDay.getDate(); d++) {
    const date = new Date(year, month, d);
    const isToday =
      today.getFullYear() === year &&
      today.getMonth() === month &&
      today.getDate() === d;

    const dayEvents = events.filter((e) => {
      const ev = new Date(e.starts_at);
      return ev.getFullYear() === year &&
             ev.getMonth() === month &&
             ev.getDate() === d;
    });

    cells.push({ date, isCurrentMonth: true, isToday, events: dayEvents });
  }

  // pad to complete 6 rows (42 cells)
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    const date = new Date(year, month + 1, d);
    cells.push({ date, isCurrentMonth: false, isToday: false, events: [] });
  }

  return cells;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function EventCalendar({
  year, month, events,
  onPrev, onNext,
  onDayClick, onEventClick,
}: EventCalendarProps) {
  const grid = useMemo(() => buildGrid(year, month, events), [year, month, events]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold text-ink">
          {MONTH_NAMES[month]} <span className="text-ink-muted font-medium">{year}</span>
        </h2>
        <div className="flex items-center gap-1">
          <button
            onClick={onPrev}
            className="rounded-lg p-2 text-ink-muted hover:bg-white/[0.06] hover:text-ink transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={onNext}
            className="rounded-lg p-2 text-ink-muted hover:bg-white/[0.06] hover:text-ink transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 gap-px">
        {WEEKDAYS.map((d) => (
          <div
            key={d}
            className="pb-2 text-center font-mono text-[10px] font-semibold uppercase tracking-widest text-ink-subtle"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-px rounded-2xl overflow-hidden bg-hairline">
        {grid.map((cell, idx) => (
          <DayCell
            key={idx}
            cell={cell}
            onDayClick={onDayClick}
            onEventClick={onEventClick}
          />
        ))}
      </div>
    </div>
  );
}

// ── Day cell ──────────────────────────────────────────────────────────────────

interface DayCellProps {
  cell: CalendarDay;
  onDayClick: (date: Date, events: Event[]) => void;
  onEventClick: (event: Event) => void;
}

function DayCell({ cell, onDayClick, onEventClick }: DayCellProps) {
  const MAX_VISIBLE = 2;
  const visible = cell.events.slice(0, MAX_VISIBLE);
  const overflow = cell.events.length - MAX_VISIBLE;

  return (
    <div
      onClick={() => onDayClick(cell.date, cell.events)}
      className={`min-h-[100px] p-1.5 cursor-pointer transition-colors group ${
        cell.isCurrentMonth
          ? 'bg-midnight-2 hover:bg-midnight-3'
          : 'bg-midnight opacity-40 pointer-events-none'
      }`}
    >
      {/* Day number */}
      <div className="flex justify-end mb-1">
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition-all ${
            cell.isToday
              ? 'bg-accent text-white'
              : 'text-ink-muted group-hover:text-ink'
          }`}
        >
          {cell.date.getDate()}
        </span>
      </div>

      {/* Events */}
      <div className="space-y-0.5">
        {visible.map((ev) => (
          <EventPill
            key={ev.id}
            event={ev}
            onClick={(e) => {
              e.stopPropagation();
              onEventClick(ev);
            }}
          />
        ))}
        {overflow > 0 && (
          <div className="text-[10px] text-ink-subtle pl-1">+{overflow} mais</div>
        )}
      </div>
    </div>
  );
}

// ── Event pill ─────────────────────────────────────────────────────────────────

function EventPill({ event, onClick }: { event: Event; onClick: (e: React.MouseEvent) => void }) {
  const colorCfg = EVENT_COLOR_MAP[event.color];
  const hour = new Date(event.starts_at).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group/pill w-full flex items-center gap-1 rounded px-1.5 py-0.5 text-left text-[10px] leading-tight
        bg-midnight-3 border-l-2 hover:opacity-90 transition-opacity ${colorCfg.card}`}
    >
      <span className="text-ink-subtle shrink-0">{hour}</span>
      <span className="truncate text-ink font-medium">{event.title}</span>
    </button>
  );
}

// ── Mini event list (used in day detail panel) ────────────────────────────────

interface EventListItemProps {
  event: Event;
  onClick: () => void;
}

export function EventListItem({ event, onClick }: EventListItemProps) {
  const colorCfg = EVENT_COLOR_MAP[event.color];
  const start = new Date(event.starts_at);
  const timeLabel = start.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const dateLabel = start.toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group w-full flex items-start gap-3 rounded-xl p-3.5 border-l-4 bg-midnight-2/80 hover:bg-midnight-3 transition-colors text-left ${colorCfg.card}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink truncate">{event.title}</p>
        <p className="text-[11px] text-ink-subtle mt-0.5">
          {dateLabel} · {timeLabel}
          {event.location_name && ` · ${event.location_name}`}
        </p>
      </div>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${EVENT_STATUS_COLORS[event.status]}`}>
        {EVENT_STATUS_LABELS[event.status]}
      </span>
    </button>
  );
}
